import { FileSignature, Gem } from "lucide-react"
import type { ClientTheme } from "@/app/lib/themes"
import type { InterestKind } from "@/app/pages/NewTransactionPage/types"

export const KIND_LABELS: Record<InterestKind, string> = {
  leasehold: "Leasehold",
  mineral: "Mineral",
}

/** The kind's identity icon in its accent color: leasehold = the signed
 *  lease (FileSignature), mineral = the ore (Gem). Neither icon is used
 *  elsewhere in the app, so they read unambiguously as kind markers. */
export function KindIcon({
  kind,
  size = 13,
  theme,
}: {
  kind: InterestKind
  size?: number
  theme: ClientTheme
}) {
  const Icon = kind === "leasehold" ? FileSignature : Gem
  return (
    <Icon
      size={size}
      strokeWidth={1.75}
      aria-hidden
      style={{ color: theme.kindAccents[kind], flexShrink: 0 }}
    />
  )
}

/**
 * Small identity pill for an interest kind: kind icon + label on a tinted
 * field, using the theme's kindAccents (leasehold = ink blue, mineral =
 * copper). Rendered where a kind needs to be tagged inline — e.g. the
 * transaction edit modal header — with the same colors as the page-level
 * tabs.
 */
export function KindBadge({ kind, theme }: { kind: InterestKind; theme: ClientTheme }) {
  const accent = theme.kindAccents[kind]
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full"
      style={{
        fontFamily: "var(--font-ui)",
        fontSize: "11px",
        fontWeight: 600,
        padding: "2px 9px",
        color: accent,
        backgroundColor: `${accent}17`,
        border: `1px solid ${accent}40`,
        whiteSpace: "nowrap",
      }}
    >
      <KindIcon kind={kind} size={11} theme={theme} />
      {KIND_LABELS[kind]}
    </span>
  )
}
